import { useRef, useState } from 'react';
import { Check, Gauge, Minus, Wifi } from 'lucide-react';
import { api } from '../../lib/api';
import { rememberBandwidth } from '../../lib/net';
import { useT } from '../../lib/i18n';
import { gsap, useGSAP } from '../../motion/gsap';
import { shouldAnimate } from '../../lib/format';
import { report } from '../../lib/telemetry';
import { canShareScreen } from '../share/device';
import { useUploads } from '../upload/uploads';
import { measureDown, measurePing, measureUp } from './speedMeasure';

// Tests the path the video and screen share actually travel: this browser ⇄ the server.
// Ping (median + jitter), parallel download streams, then parallel uploads.

const LADDER = [
  { label: '4K · 60 fps', mbps: 25 },
  { label: '4K', mbps: 15 },
  { label: '1080p', mbps: 8 },
  { label: '720p', mbps: 4 },
];

type Phase = 'idle' | 'ping' | 'down' | 'up' | 'done' | 'error';

// What a screen share from this device can reach: the smaller of what the upload carries (the
// sharer sends one copy to the server; ~0.07 bit per pixel per frame at 30 fps) and the screen's
// own size: a share is never sharper than the display it comes from.
const SHARE_STEPS = [
  { h: 2160, mbps: 18 },
  { h: 1440, mbps: 9 },
  { h: 1080, mbps: 5 },
  { h: 720, mbps: 2.5 },
];
function shareVerdictFor(up: number): string {
  if (!canShareScreen) return "this browser can't share its screen";
  const dpr = window.devicePixelRatio || 1;
  const sw = Math.round(screen.width * dpr);
  const sh = Math.round(screen.height * dpr);
  const screenH = Math.min(sw, sh);
  const byLink = SHARE_STEPS.find((s) => up >= s.mbps)?.h ?? 480;
  if (byLink >= screenH) return `${Math.max(sw, sh)}×${screenH} (your screen's full size)`;
  return `about ${byLink}p (your upload is the limit; the screen is ${Math.max(sw, sh)}×${screenH})`;
}

function verdictFor(down: number) {
  return LADDER.find((l) => down >= l.mbps)?.label ?? '480p';
}

export function SpeedTest() {
  const t = useT();
  const uploading = useUploads((s) => s.uploads.some((u) => u.status === 'uploading' || u.status === 'starting'));
  const [phase, setPhase] = useState<Phase>('idle');
  const [live, setLive] = useState(0);
  const [res, setRes] = useState<Result | null>(null);
  const [err, setErr] = useState('');
  const card = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const big = useRef<HTMLElement>(null);
  const shown = useRef(0);

  const animateTo = (v: number) => {
    const el = big.current;
    if (!el) return;
    const o = { v: shown.current };
    gsap.to(o, { v, duration: 0.4, ease: 'power1.out', onUpdate: () => (el.textContent = o.v >= 100 ? o.v.toFixed(0) : o.v.toFixed(1)) });
    shown.current = v;
  };
  const progress = (p: number) => track.current && gsap.to(track.current, { width: `${Math.min(100, p * 100)}%`, duration: 0.3, ease: 'none' });

  useGSAP(
    () => {
      if (phase === 'done' && shouldAnimate()) gsap.from('.ladder-row', { x: -10, opacity: 0, stagger: 0.07, duration: 0.4 });
    },
    { scope: card, dependencies: [phase] },
  );

  const run = async () => {
    setErr('');
    setRes(null);
    try {
      setPhase('ping');
      progress(0);
      const { ping, jitter } = await measurePing((p) => progress(p / 3));
      setPhase('down');
      const down = await measureDown((mbps, p) => {
        setLive(mbps);
        animateTo(mbps);
        progress(1 / 3 + p / 3);
      });
      animateTo(down);
      setPhase('up');
      const up = await measureUp((p) => progress(2 / 3 + p / 3));
      progress(1);
      setRes({ down, up, ping, jitter });
      setPhase('done');
      rememberBandwidth(down * 1e6 * 0.85);
      api.speedResult({ downMbps: +down.toFixed(1), upMbps: +up.toFixed(1), pingMs: +ping.toFixed(1), jitterMs: +jitter.toFixed(1), verdict: verdictFor(down) }).catch(() => undefined);
    } catch (e) {
      setPhase('error');
      setErr((e as Error).message);
      report('warn', 'speed', `speed test failed: ${(e as Error).message}`);
    }
  };

  const running = phase === 'ping' || phase === 'down' || phase === 'up';
  const down = res?.down ?? live;
  return (
    <section className="card card-dark" data-anim ref={card}>
      <div className="insight-head">
        <Wifi size={15} className="accent-ic" />
        {t('speed.title')}
        <span className="mono">{running ? phase : res ? 'done' : ''}</span>
      </div>
      <p className="insight-note" style={{ marginTop: 8 }}>
        {t('speed.sub')}
      </p>
      <div className="speed-big">
        <b ref={big}>{res ? down.toFixed(down >= 100 ? 0 : 1) : '0.0'}</b>
        <span>Mbps {phase === 'up' ? '↓ · measuring ↑' : '↓'}</span>
      </div>
      <div className="speed-track">
        <div ref={track} />
      </div>
      <SpeedCells res={res} />
      {uploading && (
        <p className="insight-note" style={{ color: '#f5c26b', margin: '0 0 10px' }}>
          A film is uploading from this device. It fills your connection, so the results (and any screen share) will be much lower until it finishes or you pause it.
        </p>
      )}
      {res && <SpeedVerdict res={res} />}
      {err && <p style={{ color: '#ff9b93', fontSize: 12.5, margin: '0 0 10px' }}>{err}</p>}
      <button className="btn btn-accent btn-sm" disabled={running} onClick={run}>
        <Gauge size={14} /> {res ? t('speed.again') : t('speed.run')}
      </button>
    </section>
  );
}

type Result = { down: number; up: number; ping: number; jitter: number };
const mbpsText = (v: number) => v.toFixed(v >= 100 ? 0 : 1);

function SpeedCells({ res }: { res: Result | null }) {
  const t = useT();
  return (
    <div className="speed-row">
      <div className="speed-cell">
        <small>{t('speed.down')}</small>
        <b>{res ? mbpsText(res.down) : '—'}</b>
      </div>
      <div className="speed-cell">
        <small>{t('speed.up')}</small>
        <b>{res ? mbpsText(res.up) : '—'}</b>
      </div>
      <div className="speed-cell">
        <small>{t('speed.ping')}</small>
        <b>{res ? `${res.ping.toFixed(0)} ms` : '—'}</b>
      </div>
    </div>
  );
}

function SpeedVerdict({ res }: { res: Result }) {
  const t = useT();
  return (
    <>
      <div className="insight-note" style={{ margin: '0 0 6px' }}>
        {t('speed.verdict')}: <b style={{ color: '#fff' }}>{verdictFor(res.down)}</b>
        {res.jitter > 30 ? ' · jitter is high, prefer a wired connection' : ''}
      </div>
      <div className="insight-note" style={{ margin: '0 0 6px' }}>
        Screen share from this device: <b style={{ color: '#fff' }}>{shareVerdictFor(res.up)}</b> <span className="mono">· {res.up.toFixed(0)} Mbps up</span>
      </div>
      <div className="ladder">
        {LADDER.map((l) => (
          <div key={l.label} className={`ladder-row${res.down >= l.mbps ? ' ok' : ''}`}>
            {res.down >= l.mbps ? <Check size={13} className="accent-ic" /> : <Minus size={13} />}
            {l.label}
            <span className="mono">{l.mbps} Mbps</span>
          </div>
        ))}
      </div>
    </>
  );
}
